import { useEffect, useRef, useState } from "preact/hooks";
import api from "../services/api";
import { markdownToSafeHtml } from "../utils/markdown";
import { RoutePropsForPath } from "preact-iso";
import classnames from "classnames";
import style from "./PostDetail.scss";
import monoStyle from "~/styles/mono.scss";
import hljs, { langAliasMap } from "~/utils/highlight";

type Post = {
  id: number;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
};

export default function PostDetail(props: RoutePropsForPath<"/post/:id">) {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!props.params.id) return;
    (async () => {
      try {
        const res = await api.get<Post>(`/api/posts/${props.params.id}`);
        setPost(res.data);
      } catch (e: any) {
        setError(e?.message || "加载失败");
      } finally {
        setLoading(false);
      }
    })();
  }, [props.params.id]);

  // 渲染后对代码块执行 highlight.js
  useEffect(() => {
    if (!post || !contentRef.current) return;
    const blocks = contentRef.current.querySelectorAll("pre code");
    blocks.forEach((block) => {
      const el = block as HTMLElement;

      // 处理 language-xxx 这种 class，做一次别名映射，确保用到我们注册过的语言
      const langClass =
        Array.from(el.classList).find((c) => c.startsWith("language-")) || "";

      if (langClass) {
        const raw = langClass.replace(/^language-/, "").toLowerCase();
        // 去掉连字符等，兼容 javascript-react / typescript-react 这类写法
        const compact = raw.replace(/[^a-z0-9]/g, "");
        const mapped =
          (langAliasMap as Record<string, string>)[raw] ||
          (langAliasMap as Record<string, string>)[compact] ||
          raw;

        // 清理原有 language-*，改为我们映射后的 language-mapped
        Array.from(el.classList)
          .filter((c) => c.startsWith("language-"))
          .forEach((c) => el.classList.remove(c));

        if (mapped) {
          el.classList.add(`language-${mapped}`);
        }
      }

      hljs.highlightElement(el);

      // 为所属的 pre 添加一键复制按钮（仅添加一次）
      const pre = el.closest("pre");
      if (!pre) return;
      if (pre.querySelector(`.${monoStyle.copyButton}`)) return;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = "复制";
      btn.className = monoStyle.copyButton;
      btn.addEventListener("click", () => {
        const codeText = el.innerText;
        const doCopy = async () => {
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              await navigator.clipboard.writeText(codeText);
            } else {
              const textarea = document.createElement("textarea");
              textarea.value = codeText;
              textarea.style.position = "fixed";
              textarea.style.left = "-9999px";
              document.body.appendChild(textarea);
              textarea.select();
              document.execCommand("copy");
              document.body.removeChild(textarea);
            }
            const prev = btn.textContent;
            btn.textContent = "已复制";
            setTimeout(() => {
              btn.textContent = prev;
            }, 1500);
          } catch {
            const prev = btn.textContent;
            btn.textContent = "复制失败";
            setTimeout(() => {
              btn.textContent = prev;
            }, 1500);
          }
        };
        void doCopy();
      });

      pre.appendChild(btn);
    });
  }, [post]);

  if (loading) return <div>加载中...</div>;
  if (error) return <div>错误：{error}</div>;
  if (!post) return <div>未找到该博客</div>;

  return (
    <div className={classnames(style.container, style.postDetailPage)}>
      <div className={style.pageHeader}>
        <a className={style.back} href="/">返回</a>
      </div>
      <article>
        <h1 className={style.title}>{post.title}</h1>
        <div className={style.meta}>
          创建：{new Date(post.created_at).toLocaleString()} | 更新：
          {new Date(post.updated_at).toLocaleString()}
        </div>
        <div
          ref={contentRef}
          className={classnames(monoStyle.markdownBody, monoStyle.mono, style.content)}
          dangerouslySetInnerHTML={{ __html: markdownToSafeHtml(post.content) }}
        />
      </article>
    </div>
  );
}
